import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, X, Truck, Package, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VendorFormDialog from "./components/VendorFormDialog";

export default function Vendors() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/spares/vendors", { params: { q: q.trim() || undefined, active_only: activeOnly } })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q, activeOnly]);

  return (
    <div className="space-y-4" data-testid="spare-vendors-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Vendors</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            {rows.length} vendor{rows.length === 1 ? "" : "s"} · your local supply network.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)}
                data-testid="spare-vendors-add-btn"
                className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
          <Plus size={13} /> Add Vendor
        </button>
      </header>

      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name, contact, phone, GSTIN…"
                 data-testid="spare-vendors-search"
                 className="w-full h-10 pl-9 pr-9 rounded-lg border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
          {q && (
            <button onClick={() => setQ("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]">
              <X size={14} />
            </button>
          )}
        </div>
        <label className="inline-flex items-center gap-2 text-[12.5px] text-[#1d3557]">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}
                 data-testid="spare-vendors-active-only"
                 className="w-4 h-4 accent-[#e63946]" />
          Active only
        </label>
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="spare-vendors-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <Truck size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No vendors yet</div>
            <p className="text-[12px] text-[#667085] mt-1">Add your first supplier to start tracking purchases.</p>
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="spare-vendors-table">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                    <th className="px-5 py-2.5">Vendor</th>
                    <th className="px-3 py-2.5">Contact</th>
                    <th className="px-3 py-2.5">Phone</th>
                    <th className="px-3 py-2.5">Email</th>
                    <th className="px-3 py-2.5">GSTIN</th>
                    <th className="px-3 py-2.5">Location</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((v) => (
                    <tr key={v.id}
                        className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
                        onClick={() => nav(`/spare-parts/vendors/${v.id}`)}
                        data-testid={`spare-vendor-row-${v.id}`}>
                      <td className="px-5 py-3 font-semibold text-[#1d3557]">{v.name}</td>
                      <td className="px-3 py-3 text-[#667085]">{v.contact_person || "—"}</td>
                      <td className="px-3 py-3 font-mono">{v.phone || "—"}</td>
                      <td className="px-3 py-3 text-[#667085]">{v.email || "—"}</td>
                      <td className="px-3 py-3 font-mono text-[11.5px]">{v.gstin || "—"}</td>
                      <td className="px-3 py-3 text-[#667085]">{[v.city, v.state].filter(Boolean).join(", ") || "—"}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${
                          v.active ? "bg-[#12B76A]/12 text-[#0F9256]" : "bg-[#98A2B3]/20 text-[#475467]"
                        }`}>
                          {v.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right text-[#a8dadc]">→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="md:hidden divide-y divide-[#F1FAEE]">
              {rows.map((v) => (
                <li key={v.id}>
                  <Link to={`/spare-parts/vendors/${v.id}`} className="block p-4 hover:bg-[#F7F9FC]"
                        data-testid={`spare-vendor-card-${v.id}`}>
                    <div className="text-[13px] font-semibold text-[#1d3557]">{v.name}</div>
                    {v.contact_person && <div className="text-[11.5px] text-[#667085]">{v.contact_person}</div>}
                    <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-[#667085]">
                      {v.phone && <span className="inline-flex items-center gap-1"><Phone size={10}/> {v.phone}</span>}
                      {v.email && <span className="inline-flex items-center gap-1"><Mail size={10}/> {v.email}</span>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <VendorFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => { setCreateOpen(false); load(); }}
      />
    </div>
  );
}
